/**
 * "Add to home screen" affordance.
 *
 * This app is used one-handed on a phone during a game, so being installed is
 * the difference between a bookmark and something people actually open. The
 * two platforms need completely different handling:
 *
 *   - Chrome / Edge / Android fire `beforeinstallprompt`, which is stashed and
 *     replayed from a button of our own.
 *   - iOS Safari fires nothing and has no API. The only route is Share -> Add
 *     to Home Screen, so all we can do is say so.
 *
 * All of the detection lives in lib/install.ts, which starts listening at
 * import time -- see the note there on why a listener inside this component
 * misses the event entirely.
 */

import { useEffect, useState } from 'react';
import {
  dismissInstallPrompt,
  getInstallState,
  runInstallPrompt,
  subscribeToInstallState,
  type InstallState,
} from '../lib/install';

export function InstallPrompt() {
  const [state, setState] = useState<InstallState>(getInstallState);

  useEffect(() => subscribeToInstallState(setState), []);

  if (state.installed || state.dismissed) return null;
  if (!state.deferred && !state.iosHint) return null;

  const canPrompt = state.deferred !== null;

  return (
    <div className="card install-prompt" role="region" aria-label="Install this app">
      <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}>
        <img src="/icon-192.png" alt="" width={40} height={40} style={{ borderRadius: 8 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <strong style={{ display: 'block' }}>Add to your home screen</strong>
          <span className="small muted">
            {canPrompt
              ? 'Opens full screen, straight to your games.'
              : 'Tap Share, then \u201cAdd to Home Screen\u201d.'}
          </span>
        </div>
        {canPrompt && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => void runInstallPrompt()}
          >
            Install
          </button>
        )}
        <button
          type="button"
          className="btn-ghost"
          onClick={dismissInstallPrompt}
          aria-label="Dismiss until next visit"
        >
          {'\u2715'}
        </button>
      </div>
    </div>
  );
}
